/**
 * CrazyGames SDK (v3) glue for portal builds (`VITE_PORTAL=crazygames`, see tools/build-crazygames.mjs and
 * docs/crazygames.md). The script tag is added to index.html by the build; nothing here runs in any other build.
 *
 * The game has to work without the SDK: ad blockers can stop its script from loading, and outside CrazyGames
 * (localhost, a preview on another domain) every call throws. So every call is guarded and failures are ignored.
 */
import { signal } from '@preact/signals';
import { audio } from '../game/audio/Audio';
import { portal } from './platform';

interface CrazySettings {
  disableChat?: boolean;
  muteAudio?: boolean;
}

interface CrazySdk {
  environment?: 'local' | 'crazygames' | 'disabled';
  init(): Promise<void>;
  game: {
    gameplayStart(): unknown;
    gameplayStop(): unknown;
    loadingStart(): unknown;
    loadingStop(): unknown;
    settings?: CrazySettings;
    addSettingsChangeListener?(listener: (settings: CrazySettings) => void): void;
  };
}

declare global {
  interface Window {
    CrazyGames?: { SDK?: CrazySdk };
  }
}

/** The portal asked for chat to be off (SDK `settings.disableChat`): no lobby chat, no in-match chat, incoming or outgoing. */
export const portalChatDisabled = signal(false);

let sdk: CrazySdk | null = null;
let playing = false;
let loadingStarted = false;
let loadingStopped = false;

const INIT_TIMEOUT_MS = 8000;

/** Calls an SDK method, ignoring errors (thrown or as a rejected promise). */
function call(fn: () => unknown): void {
  try {
    const r = fn();
    if (r && typeof (r as Promise<unknown>).catch === 'function') (r as Promise<unknown>).catch(() => {});
  } catch {
    /* the SDK is optional */
  }
}

function applySettings(s: CrazySettings | undefined): void {
  portalChatDisabled.value = !!s?.disableChat;
  audio.setExternalMute(!!s?.muteAudio);
}

/** Initializes the SDK (it must be, once, before any other call). Resolves either way; `sdk` stays null when it is not usable. */
export async function initPortalSdk(): Promise<void> {
  if (portal !== 'crazygames' || sdk) return;
  const candidate = window.CrazyGames?.SDK;
  if (!candidate) return;
  try {
    await Promise.race([candidate.init(), new Promise<never>((_, reject) => setTimeout(() => reject(new Error('SDK init timed out')), INIT_TIMEOUT_MS))]);
    // 'disabled' = a domain that is neither CrazyGames nor local: every method would throw.
    if (candidate.environment === 'disabled') return;
    sdk = candidate;
  } catch {
    return;
  }
  applySettings(sdk.game.settings);
  call(() => sdk?.game.addSettingsChangeListener?.((s) => applySettings(s)));
  if (!loadingStarted) {
    loadingStarted = true;
    call(() => sdk?.game.loadingStart());
  }
}

/** The first screen is up (menus, or a match): tells the portal loading is over. Once. */
export function portalLoadingDone(): void {
  if (!sdk || !loadingStarted || loadingStopped) return;
  loadingStopped = true;
  call(() => sdk?.game.loadingStop());
}

/**
 * Whether the player is actively playing (not in a menu, the pause screen, typing chat or on the results). The portal
 * wants gameplayStart when play begins or resumes and gameplayStop on every break, and nothing for the tab losing focus.
 */
export function setPortalGameplay(active: boolean): void {
  if (!sdk || active === playing) return;
  playing = active;
  call(() => (active ? sdk?.game.gameplayStart() : sdk?.game.gameplayStop()));
}

/** Test hook: forget the SDK and its state. */
export function resetPortalForTests(): void {
  sdk = null;
  playing = false;
  loadingStarted = false;
  loadingStopped = false;
  portalChatDisabled.value = false;
}
